import React from 'react';
import PropTypes from 'prop-types';
import { ConfirmationModal } from './ConfirmationModal';
import { i18n } from '../i18n/translate';
import { loadExternalData } from '../utils/ApiUtil';
import { WindowHelper } from '../utils/WindowHelper';

const POLL_INTERVAL = 60000;
const TIMEOUT_WARNING_MS = 90000;
const SESSION_EXTENDING_EVENTS = ['load', 'mousemove', 'mousedown', 'click', 'scroll', 'keypress'];

export class AutoLogout extends React.Component {
  static propTypes = {
    clearToken: PropTypes.func,
    modalBody: PropTypes.node,
    modalTitle: PropTypes.string,
    redirectUrl: PropTypes.string,
    sessionUrl: PropTypes.string,
    authToken: PropTypes.string,
    renderAsLegacy: PropTypes.bool,
  };

  static defaultProps = {
    clearToken: () => {},
    redirectUrl: '',
    sessionUrl: '/oauth/token/info',
  };

  constructor(props) {
    super(props);

    this.state = {
      logout: false,
      warning: false,
    };

    this.isMountedFlag = false;
    this.pollSessionStatus = null;
  }

  componentDidMount() {
    this.isMountedFlag = true;
    this.checkAndUpdateSession();
    this.startPolling();
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
    this.stopPolling();
    this.removeEventListeners();
  }

  startPolling = () => {
    if (this.pollSessionStatus) return;

    this.pollSessionStatus = setInterval(() => {
      if (!this.state.logout) {
        this.checkAndUpdateSession();
      } else {
        this.stopPolling();
        WindowHelper.reload();
      }
    }, POLL_INTERVAL);
  };

  stopPolling = () => {
    if (!this.pollSessionStatus) return;
    clearInterval(this.pollSessionStatus);
    this.pollSessionStatus = null;
  };

  safeSetState = nextState => {
    if (!this.isMountedFlag) return;
    this.setState(nextState);
  };

  checkAndUpdateSession = async () => {
    if (!this.isMountedFlag) return;

    let isExpired;
    let secondsLeft;
    try {
      const session = await this.getSessionStatus();
      const expiresIn = Number(session?.expires_in);
      isExpired = !Number.isFinite(expiresIn) || expiresIn <= 0;
      secondsLeft = Number.isFinite(expiresIn) ? expiresIn : 0;
    } catch (e) {
      isExpired = true;
      secondsLeft = 0;
    }

    this.safeSetState({
      logout: isExpired,
      warning: !isExpired && secondsLeft * 1000 <= TIMEOUT_WARNING_MS,
    });
  };

  getSessionStatus = async () => await loadExternalData(this.props.sessionUrl, this.props.authToken, false);

  addEventListeners = () => {
    SESSION_EXTENDING_EVENTS.map(event => {
      window.addEventListener(event, this.extendSession);
    });
  };

  removeEventListeners = () => {
    SESSION_EXTENDING_EVENTS.map(event => {
      window.removeEventListener(event, this.extendSession);
    });
  };

  onWarningAction = () => {
    this.safeSetState({ warning: false });
    this.addEventListeners();
    this.extendSession();
  };

  redirect = () => {
    if (this.props.redirectUrl) {
      window.location.href = encodeURI(this.props.redirectUrl);
    } else {
      WindowHelper.reload();
    }
  };

  extendSession = () => {
    this.props.clearToken();
    this.redirect();
  };

  get modalTitle() {
    return this.props.modalTitle || i18n.t('Your session has expired.');
  }

  get modalBody() {
    return (
      this.props.modalBody || (
        <div>{i18n.t('Unfortunately, your session has expired and we require you to login again.')}</div>
      )
    );
  }

  render() {
    return (
      <div>
        {this.state.logout && (
          <ConfirmationModal
            title={this.modalTitle}
            onConfirm={this.redirect}
            canClose={false}
            show={true}
            confirmOnly={true}
          >
            {this.modalBody}
          </ConfirmationModal>
        )}
        {this.state.warning && (
          <ConfirmationModal
            renderAsLegacy={this.props.renderAsLegacy}
            title={i18n.t('Your session is about to expire.')}
            onConfirm={this.onWarningAction}
            canClose={false}
            show={true}
            confirmOnly={true}
            confirmButtonText={i18n.t('Yes')}
          >
            {i18n.t('Would you like to stay logged in?')}
          </ConfirmationModal>
        )}
      </div>
    );
  }
}
